/**
 * The navbar search bar's typewriter teaser as a plain list of timed frames (Sage search bar,
 * 2026-09-28). Pure, so it's unit-testable and the React hook (useSearchBarTeaser) only has to
 * schedule what this returns. The prerendered static twin (landing-nav-live-index-script.ts)
 * can't import this at runtime, so it mirrors the same rules in vanilla JS and reads its timing
 * from TEASER_TIMING below.
 *
 * Rules: type a sample one character at a time; the count only changes once a whole word is
 * typed (so it never shows a count for half a word, which the SRP would never print); hold on
 * the full search; erase faster than it typed, stepping the count back up word by word; pause;
 * next sample. At most two samples per session.
 */
export interface TeaserStepLike {
  text: string;
  count: number;
}

export interface TeaserSampleLike {
  query: string;
  steps: TeaserStepLike[];
}

export interface TeaserFrame {
  /** Milliseconds after the teaser starts. */
  atMs: number;
  text: string;
  /** Count for the words typed so far; null means "no whole word yet", so the host shows its
   * own idle count. */
  count: number | null;
}

export const TEASER_TIMING = {
  /** Wait after the bar mounts before the first character. */
  startDelayMs: 1800,
  typeMs: 85,
  eraseMs: 28,
  holdMs: 1700,
  gapMs: 700,
  maxSamples: 2,
  /** Only on desktop widths where the bar has room for the What slot label (Tailwind xl). */
  minViewportPx: 1280,
} as const;

/** Played once per browser session: set when the teaser finishes or the visitor interrupts it. */
export const TEASER_SESSION_KEY = 'jc.searchTeaser.played';

export function teaserCountFor(text: string, steps: TeaserStepLike[]): number | null {
  let count: number | null = null;
  for (const step of steps) {
    if (text === step.text || text.startsWith(`${step.text} `)) count = step.count;
  }
  return count;
}

export function buildTeaserFrames(samples: TeaserSampleLike[], timing: Partial<typeof TEASER_TIMING> = {}): TeaserFrame[] {
  const t = { ...TEASER_TIMING, ...timing };
  const frames: TeaserFrame[] = [];
  let at = 0;
  samples.slice(0, t.maxSamples).forEach((sample, index) => {
    if (index > 0) at += t.gapMs;
    const query = sample.query;
    for (let i = 1; i <= query.length; i++) {
      const text = query.slice(0, i);
      frames.push({ atMs: at, text, count: teaserCountFor(text, sample.steps) });
      at += t.typeMs;
    }
    at += t.holdMs;
    for (let i = query.length - 1; i >= 0; i--) {
      const text = query.slice(0, i);
      frames.push({ atMs: at, text, count: teaserCountFor(text, sample.steps) });
      at += t.eraseMs;
    }
  });
  return frames;
}
